<template>
  <div
    :style="mainStyle"
    :class="[isResizing && 'cursor-col-resize']"
    ref="mainEl"
  >
    <slot />
  </div>

  <aside
    v-show="asideColumnIsOpen"
    :class="[
      props.border,
      'fixed right-0 bottom-0 top-16 overflow-y-auto',
      isResizing && 'cursor-col-resize',
    ]"
    :style="slideoverStyle"
  >
    <div id="slideover-content" class="h-full"></div>
    <div
      @mousedown.prevent="startResizing"
      class="absolute inset-0 cursor-col-resize w-2 h-full"
    ></div>
  </aside>
</template>

<script setup>
import {
  useElementBounding,
  useElementSize,
  useEventListener,
  useParentElement,
} from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import {
  useProvideSlideoverStore,
  useSlideoverStore,
} from './useSlideoverStore'

const props = defineProps({
  border: String,
})

useProvideSlideoverStore()

const { asideColumnIsOpen, slideoverSize } = useSlideoverStore()

// Resizing
const parentEl = useParentElement()
const { width: parentWidth, x: parentX } = useElementBounding(parentEl)

const mainEl = ref(null)
const { width: mainWidth } = useElementSize(mainEl)

const minimumWidth = 384

const isResizing = ref(false)

const startResizing = () => {
  isResizing.value = true
  let cleanupMousemove,
    cleanupMouseup = null

  const stopResizing = () => {
    isResizing.value = false

    if (cleanupMousemove) cleanupMousemove()
    if (cleanupMouseup) cleanupMouseup()
  }

  cleanupMousemove = useEventListener(window, 'mousemove', doResize)
  cleanupMouseup = useEventListener(window, 'mouseup', stopResizing)
}

function doResize(event = null) {
  if (!isResizing.value && event) return

  let newWidth = slideoverSize.value
  if (event) {
    newWidth = Math.max(
      ...[parentX.value + parentWidth.value - event.clientX, minimumWidth]
    )
  }

  if (
    mainWidth.value <= minimumWidth &&
    (newWidth > slideoverSize.value || !event)
  ) {
    newWidth = slideoverSize.value + mainWidth.value - minimumWidth
  }

  slideoverSize.value = newWidth
}

watch(mainWidth, () => {
  // If the main width changes from something outside of the resizing loop, call doResize to make sure the slideover shrinks if needed
  if (!isResizing.value) doResize()
})

const mainStyle = computed(() => ({
  paddingRight: asideColumnIsOpen.value ? `${slideoverSize.value}px` : '0',
}))

const slideoverStyle = computed(() => ({
  width: `${slideoverSize.value}px`,
}))
</script>
